/*
 * component-launch-bundle.js
 * Launch Bundle Offer — pick N bars + M deodorants, get the accessories free.
 *
 * Powers the <launch-bundle-offer> custom element rendered by
 * sections/launch-bundle-offer.liquid. Liquid renders every card, slot stage,
 * gift row and price; this script only adds interactivity:
 *   - quantity picking per family (soap / deo) with a hard cap per family
 *   - slot-stage rendering (dashed boxes fill as the shopper picks)
 *   - running price that mirrors checkout (per-family tier curves, the
 *     subscribe discount, per-line cent rounding, discount-code cookie)
 *   - submit: Loop bundle transaction (sub mode) → /cart/add.js → optional
 *     gift discount code via /cart/update.js → checkout or cart drawer
 *
 * The pricing helpers are plain top-level functions (no DOM) so the story
 * tests can extract and run them against the shipped source.
 */

/* ---------------------------------------------------------------- pricing */

/* Parse "count:pct,count:pct" into an ascending [{count, pct}] list. */
function parseTierDiscounts(raw, fallback) {
  return String(raw || fallback || '')
    .split(',')
    .map(function (pair) {
      var bits = pair.split(':');
      return { count: parseInt(bits[0], 10), pct: parseFloat(bits[1]) };
    })
    .filter(function (t) { return !isNaN(t.count) && !isNaN(t.pct); })
    .sort(function (a, b) { return a.count - b.count; });
}

/* Highest tier whose threshold the unit count has reached; 0 below the first. */
function tierDiscountPercentFor(count, tiers) {
  var pct = 0;
  for (var i = 0; i < tiers.length; i++) {
    if (count >= tiers[i].count) pct = tiers[i].pct;
  }
  return pct;
}

/* Combined tier + subscribe multiplier for one line. `stacking` is
   'multiplicative' (tier × sub — matches a Loop selling plan under an
   automatic discount) or 'additive' (percentage points sum). */
function familyMultiplier(count, tiers, stacking, subPct) {
  var tierPct = tierDiscountPercentFor(count, tiers);
  if (stacking === 'multiplicative') {
    return ((100 - tierPct) / 100) * ((100 - subPct) / 100);
  }
  return (100 - tierPct - subPct) / 100;
}

function roundCents(n) { return Math.round(n * 100) / 100; }

/* Price one family. `lines` is [{price, qty}] in dollars. Mirrors Shopify's
   checkout math: tier + subscribe round PER ITEM then × qty; a discount code
   rounds PER LINE on the already-discounted line total. */
function priceFamily(lines, tiers, stacking, subPct, codeMult) {
  var units = 0;
  for (var i = 0; i < lines.length; i++) units += lines[i].qty;
  var mult = familyMultiplier(units, tiers, stacking, subPct);
  var acc = { raw: 0, discounted: 0, units: units };
  for (var j = 0; j < lines.length; j++) {
    var line = lines[j];
    acc.raw += line.price * line.qty;
    var lineTierSub = roundCents(line.price * mult) * line.qty;
    acc.discounted += roundCents(lineTierSub * codeMult);
  }
  return acc;
}

function countUnits(lines) {
  var n = 0;
  for (var i = 0; i < lines.length; i++) n += lines[i].qty;
  return n;
}

/* Fill the unpicked slots of a family with base-priced placeholders so the
   headline price always describes the COMPLETE bundle ("6 bars + 2 sticks")
   and only moves when a premium scent is picked. */
function withPlaceholders(lines, requiredCount, basePrice) {
  var missing = requiredCount - countUnits(lines);
  if (missing <= 0) return lines.slice();
  return lines.concat([{ price: basePrice, qty: missing, placeholder: true }]);
}

/*
 * Whole-bundle totals.
 *   cfg: { soapCount, soapBase, soapTiers, soapStacking,
 *          deoCount,  deoBase,  deoTiers,  deoStacking,
 *          subPct, codeMult, gifts: [{price}] }
 *   state: { soap: [{price, qty}], deo: [{price, qty}] }
 * Gifts are always $0 in `discounted` and full price in `raw`, so
 * `saved` carries the gift value plus every tier / subscribe saving.
 */
function computeBundleTotals(cfg, state) {
  var subPct = cfg.subPct || 0;
  var codeMult = cfg.codeMult == null ? 1 : cfg.codeMult;
  var pickedSoap = countUnits(state.soap || []);
  var pickedDeo = countUnits(state.deo || []);
  var soapLines = withPlaceholders(state.soap || [], cfg.soapCount, cfg.soapBase);
  var deoLines = withPlaceholders(state.deo || [], cfg.deoCount, cfg.deoBase);
  var soap = priceFamily(soapLines, cfg.soapTiers, cfg.soapStacking, subPct, codeMult);
  var deo = priceFamily(deoLines, cfg.deoTiers, cfg.deoStacking, subPct, codeMult);
  var giftValue = 0;
  (cfg.gifts || []).forEach(function (g) { giftValue += g.price; });
  var raw = roundCents(soap.raw + deo.raw + giftValue);
  /* Paid accessories (cfg.giftsPaid) are charged at their regular price in
     both plans. They take no tier, subscribe, or code discount. */
  var giftCharge = 0;
  if (cfg.giftsPaid) {
    /* A code that covers the whole bundle (cfg.codeOnGifts) also discounts
       the accessories, rounded per line like checkout does. */
    var giftMult = cfg.codeOnGifts ? codeMult : 1;
    (cfg.gifts || []).forEach(function (g) { giftCharge += roundCents(g.price * giftMult); });
  }
  var discounted = roundCents(soap.discounted + deo.discounted + giftCharge);
  return {
    raw: raw,
    discounted: discounted,
    saved: roundCents(raw - discounted),
    giftValue: roundCents(giftValue),
    soapUnits: pickedSoap,
    deoUnits: pickedDeo,
    complete: pickedSoap >= cfg.soapCount && pickedDeo >= cfg.deoCount
  };
}

/*
 * Cart lines for /cart/add.js.
 *   picks: { soap: [{variantId, qty}], deo: [{variantId, qty}] }
 *   gifts: [{variantId}]
 *   opts:  { subscribe, groupId, txnId, sellingPlan, bundleName, giftLabel,
 *            offerKey, offerHandle }
 * The offer is a simulated product: no bundle product is added. Every line
 * (bars, sticks and the free gifts) shares one `_bundle-id` and carries
 * `_offer` (e.g. "starter-with-gifts") + `_offer_handle` (the display
 * product whose image / title the cart drawer shows), so the cart renders
 * one card and discounts can target the offer. `_bundle-size` counts bars +
 * sticks only. Gifts also carry `_gift`; the subscription props and selling
 * plan go on bars + sticks only.
 */
function buildCartItems(picks, gifts, opts) {
  var items = [];
  var bundleSize = 0;
  var families = ['soap', 'deo'];
  families.forEach(function (f) {
    (picks[f] || []).forEach(function (p) { bundleSize += p.qty; });
  });
  function baseProps() {
    var props = { '_bundle-size': bundleSize, '_bundle-id': opts.groupId };
    props['_offer'] = opts.offerKey;
    if (opts.offerHandle) props['_offer_handle'] = opts.offerHandle;
    return props;
  }
  families.forEach(function (f) {
    (picks[f] || []).forEach(function (p) {
      var props = baseProps();
      if (opts.subscribe) {
        props['_bundleId'] = opts.txnId || '';
        props['_bundleName'] = opts.bundleName;
      }
      var item = { id: p.variantId, quantity: p.qty, properties: props };
      if (opts.subscribe) item.selling_plan = opts.sellingPlan;
      items.push(item);
    });
  });
  (gifts || []).forEach(function (g) {
    var props = baseProps();
    /* `_gift` is what the free-gift discount keys on, so a paid accessory
       never carries it. `_included` lets the cart list it with the bundle. */
    props[opts.giftsPaid ? '_included' : '_gift'] = opts.giftLabel || 'Launch Bundle';
    items.push({ id: g.variantId, quantity: 1, properties: props });
  });
  return items;
}

/* Merge the gift code into whatever codes the cart already carries (a MOIST
   cookie, a creator code) so attaching ours never drops theirs. Returns the
   comma-joined string /cart/update.js expects. */
function mergeDiscountCodes(existing, giftCode) {
  var seen = {};
  var out = [];
  (existing || []).forEach(function (c) {
    var code = typeof c === 'string' ? c : (c && c.code);
    if (!code) return;
    var key = code.toUpperCase();
    if (seen[key]) return;
    seen[key] = true;
    out.push(code);
  });
  if (giftCode && !seen[giftCode.toUpperCase()]) out.push(giftCode);
  return out.join(',');
}

/* "a, b,c" → ['a','b','c'] (trimmed, empties dropped). */
function parseHandleList(raw) {
  return String(raw || '').split(',').map(function (h) { return h.trim(); }).filter(Boolean);
}

/* Quiz arrivals (templates/page.soft-skin-quiz.json) carry the six
   recommended bars as ?qv=1&qb=<variant ids, comma-separated>. Returns the
   ids in slot order, or null when the link has no selection or a damaged one,
   in which case the page keeps its own pre-selected bars. Ids stay strings. */
function parseQuizSoapIds(search, slotCount) {
  var params = new URLSearchParams(search || '');
  if (!params.has('qb')) return null;
  if (params.getAll('qb').length !== 1 || params.getAll('qv').length !== 1) return null;
  if (params.get('qv') !== '1') return null;
  var raw = params.get('qb');
  if (raw.length > 125) return null;
  var ids = raw.split(',');
  if (ids.length !== slotCount) return null;
  for (var i = 0; i < ids.length; i++) {
    if (!/^[1-9][0-9]{0,19}$/.test(ids[i])) return null;
  }
  return ids;
}

/* Whole-number percent saved against the full compare price (0 when nothing saved). */
function savingsPercent(raw, discounted) {
  if (!(raw > 0) || discounted >= raw) return 0;
  return Math.round(((raw - discounted) / raw) * 100);
}

function moneyFmt(n) { return '$' + n.toFixed(2); }
function moneyFmtWhole(n) {
  return Math.abs(n - Math.round(n)) < 1e-9 ? '$' + Math.round(n) : moneyFmt(n);
}

/* --------------------------------------------------------------- element */

class LaunchBundleOffer extends HTMLElement {
  constructor() {
    super();
    this.picks = { soap: [], deo: [] }; // ordered [{variantId, price, image, label}]
  }

  connectedCallback() {
    if (this.dataset.lboBound === '1') return;
    this.dataset.lboBound = '1';

    var d = this.dataset;
    /* A deodorant count of 0 is a real setting (soap-only offers), so only a
       missing or unreadable value falls back to 2. */
    var deoCountRaw = parseInt(d.deoCount, 10);
    this.cfg = {
      soapCount: parseInt(d.soapCount, 10) || 6,
      deoCount: isNaN(deoCountRaw) || deoCountRaw < 0 ? 2 : deoCountRaw,
      giftsPaid: d.giftMode === 'paid',
      collapsePicker: d.collapsePicker === 'true',
      /* Offer discount code: attached to the cart on add, and previewed here
         so the page shows what checkout charges. Scope "all" means the code
         also covers the paid accessories; "bars" means bars and sticks only. */
      offerCode: (d.offerCode || '').trim(),
      offerCodePct: parseFloat(d.offerCodePct) || 0,
      offerCodeScope: d.offerCodeScope === 'all' ? 'all' : 'bars',
      soapBase: (parseInt(d.soapBaseCents, 10) || 700) / 100,
      deoBase: (parseInt(d.deoBaseCents, 10) || 1400) / 100,
      soapTiers: parseTierDiscounts(d.soapTiers, '8:5'),
      deoTiers: parseTierDiscounts(d.deoTiers, ''),
      soapStacking: d.soapStacking || 'multiplicative',
      deoStacking: d.deoStacking || 'multiplicative',
      subPctSetting: parseFloat(d.subPct) || 10,
      freeShipDollars: parseFloat(d.freeShipDollars) || 0,
      sellingPlan: d.sellingPlanId || '',
      loopBundleId: d.loopBundleId || '',
      loopBoxSizeId: d.loopBoxSizeId || '',
      bundleName: d.bundleName || 'Cheeky Bundle',
      giftCode: (d.giftCode || '').trim(),
      giftLabel: d.giftLabel || 'Launch Bundle',
      /* `_offer` on every line. The section always sends a name (the
         offer_key setting, or the display product's handle). There is no
         fallback to another offer's name: that would hand this offer the
         other one's discounts. */
      offerKey: (d.offerKey || '').trim(),
      offerHandle: d.offerHandle || '',
      afterAdd: d.afterAdd || 'checkout',
      ctaLabel: d.ctaLabel || 'Add to Cart',
      unit: {
        soap: { one: d.soapUnitSingular || 'bar', many: d.soapUnitPlural || 'bars' },
        deo: { one: d.deoUnitSingular || 'deodorant', many: d.deoUnitPlural || 'deodorants' }
      }
    };
    this.gifts = Array.prototype.map.call(this.querySelectorAll('[data-lbo-gift]'), function (el) {
      return { variantId: el.dataset.variantId, price: parseFloat(el.dataset.price) || 0 };
    });

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.applyDefaults();
    this.render();
    this.initScrollbars();
    this.initCollapse();
  }

  /* ----- collapsed pickers -----
     With collapse_picker on, a complete family shows only its chosen cards
     (chosen ones first, in pick order) and an Edit button. Edit opens the
     full list; Done closes it again. A family with picks missing stays open,
     because the shopper has to choose. */
  initCollapse() {
    var self = this;
    ['soap', 'deo'].forEach(function (family) {
      var group = self.querySelector('[data-lbo-group="' + family + '"]');
      if (!group) return;
      self.sortPickedFirst(family);
      if (self.cfg.collapsePicker) self.setCollapsed(family, self.unitsFor(family) >= self.requiredFor(family));
      group.removeAttribute('data-lbo-pending');
    });
  }

  /* Chosen cards move to the top of their list in pick order. The rest keep
     their merchandised order. */
  sortPickedFirst(family) {
    var list = this.querySelector('[data-lbo-group="' + family + '"] .lbo-list');
    if (!list) return;
    var seen = {};
    var anchor = list.firstElementChild;
    this.picks[family].forEach(function (pick) {
      if (seen[pick.variantId]) return;
      seen[pick.variantId] = true;
      var card = list.querySelector('[data-lbo-card][data-variant-id="' + pick.variantId + '"]');
      if (!card) return;
      if (card === anchor) { anchor = card.nextElementSibling; return; }
      list.insertBefore(card, anchor);
    });
  }

  setCollapsed(family, collapsed) {
    var group = this.querySelector('[data-lbo-group="' + family + '"]');
    if (!group) return;
    if (collapsed) this.sortPickedFirst(family);
    group.classList.toggle('is-collapsed', collapsed);
    var button = group.querySelector('[data-lbo-edit]');
    if (button) button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    var list = group.querySelector('.lbo-list');
    if (list) list.scrollTop = 0;
    /* The custom scrollbar measures the list on resize. */
    window.dispatchEvent(new Event('resize'));
  }

  isCollapsed(family) {
    var group = this.querySelector('[data-lbo-group="' + family + '"]');
    return Boolean(group && group.classList.contains('is-collapsed'));
  }

  /* Custom always-visible scrollbar for each picker list. The native bar is
     hidden in CSS; this thumb tracks scrollTop, supports drag, and the track
     jumps on click. Hidden entirely when the list fits. */
  initScrollbars() {
    this.querySelectorAll('[data-lbo-list-wrap]').forEach(function (wrap) {
      var list = wrap.querySelector('.lbo-list');
      var bar = wrap.querySelector('[data-lbo-scrollbar]');
      var thumb = bar && bar.querySelector('.lbo-scrollbar__thumb');
      if (!list || !bar || !thumb) return;

      /* Size the list so exactly `data-visible-rows` cards show before it
         scrolls (soap: the six pre-selected bars). Measured, not fixed, so
         wrapped descriptions and mobile type sizes are handled. */
      var visibleRows = parseInt(wrap.dataset.visibleRows, 10) || 0;
      function fit() {
        if (!visibleRows) return;
        var cards = list.querySelectorAll('.lbo-card');
        if (!cards.length) return;
        var last = cards[Math.min(visibleRows, cards.length) - 1];
        var top = cards[0].getBoundingClientRect().top;
        var bottom = last.getBoundingClientRect().bottom;
        var cs = getComputedStyle(list);
        var height = Math.ceil(bottom - top + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom));
        if (height > 0) list.style.maxHeight = height + 'px';
      }

      function update() {
        fit();
        var maxScroll = list.scrollHeight - list.clientHeight;
        var hasScroll = maxScroll > 1;
        wrap.classList.toggle('has-scroll', hasScroll);
        if (!hasScroll) return;
        var trackH = bar.clientHeight;
        var thumbH = Math.max(28, Math.round(trackH * (list.clientHeight / list.scrollHeight)));
        var top = Math.round((list.scrollTop / maxScroll) * (trackH - thumbH));
        thumb.style.height = thumbH + 'px';
        thumb.style.transform = 'translateY(' + top + 'px)';
      }

      var drag = null;
      thumb.addEventListener('pointerdown', function (e) {
        drag = { y: e.clientY, top: list.scrollTop };
        thumb.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      thumb.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var room = bar.clientHeight - thumb.offsetHeight;
        var maxScroll = list.scrollHeight - list.clientHeight;
        if (room <= 0) return;
        list.scrollTop = drag.top + (e.clientY - drag.y) * (maxScroll / room);
      });
      function endDrag() { drag = null; }
      thumb.addEventListener('pointerup', endDrag);
      thumb.addEventListener('pointercancel', endDrag);
      bar.addEventListener('click', function (e) {
        if (e.target === thumb) return;
        var rect = bar.getBoundingClientRect();
        var frac = (e.clientY - rect.top) / rect.height;
        var maxScroll = list.scrollHeight - list.clientHeight;
        list.scrollTop = frac * maxScroll - list.clientHeight / 2;
      });

      list.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(update);
        ro.observe(list);
        if (list.firstElementChild) ro.observe(list.firstElementChild);
      }
      window.addEventListener('resize', update, { passive: true });
      update();
      // Lazy card images can change scrollHeight after first paint.
      setTimeout(update, 500);
    });
  }

  /* Pre-selected picks from data-default-soaps / data-default-deos: one
     slot per listed handle, in order, skipping sold-out cards and stopping
     at the family cap. Mirrors the Liquid first paint so the state behind
     the pre-filled slots is real. */
  applyDefaults() {
    var self = this;
    var d = this.dataset;
    /* A quiz link fills the soap slots with exactly the bars it names. A bar
       that has since sold out leaves its slot empty for the shopper to fill;
       nothing is swapped in for it. Deodorants keep the page defaults. */
    var quizSoapIds = parseQuizSoapIds(window.location.search, this.requiredFor('soap'));
    if (quizSoapIds) {
      quizSoapIds.forEach(function (variantId) {
        var card = self.querySelector('[data-lbo-card][data-family="soap"][data-variant-id="' + variantId + '"]:not(.is-sold-out)');
        if (!card) { console.warn('[launch-bundle] quiz pick not available:', variantId); return; }
        self.pushPick(card);
      });
    }
    [['soap', quizSoapIds ? '' : d.defaultSoaps], ['deo', d.defaultDeos]].forEach(function (pair) {
      var family = pair[0];
      parseHandleList(pair[1]).forEach(function (handle) {
        if (self.unitsFor(family) >= self.requiredFor(family)) return;
        var card = self.querySelector('[data-lbo-card][data-family="' + family + '"][data-handle="' + handle + '"]:not(.is-sold-out)');
        if (!card) { console.warn('[launch-bundle] default pick not available:', family, handle); return; }
        self.pushPick(card);
      });
    });
  }

  /* ----- state helpers ----- */

  /* Selected purchase plan: 'sub' unless the One-Time card is checked. */
  isSub() {
    var checked = this.querySelector('[data-lbo-plan]:checked');
    return !checked || checked.value !== 'onetime';
  }

  /* Totals for a given plan, independent of the selected one (both cards
     show their own price). */
  totalsFor(sub) {
    return computeBundleTotals({
      soapCount: this.cfg.soapCount, soapBase: this.cfg.soapBase, soapTiers: this.cfg.soapTiers, soapStacking: this.cfg.soapStacking,
      deoCount: this.cfg.deoCount, deoBase: this.cfg.deoBase, deoTiers: this.cfg.deoTiers, deoStacking: this.cfg.deoStacking,
      subPct: sub ? this.cfg.subPctSetting : 0, codeMult: this.codeMult(), gifts: this.gifts, giftsPaid: this.cfg.giftsPaid,
      /* Scope "all": whichever code is active (the offer's own, or the
         shopper's from the discount_code cookie, e.g. MOIST) also covers the
         paid tray and bags, like an order discount on all items does. */
      codeOnGifts: this.codeMult() < 1 && this.cfg.offerCodeScope === 'all'
    }, { soap: this.linesFor('soap'), deo: this.linesFor('deo') });
  }

  subPct() { return this.isSub() ? this.cfg.subPctSetting : 0; }

  /* Storefront discount codes are always 10%; a `discount_code` cookie or
     `body.discount-applied` means checkout will take a further 10% off the
     paid lines (never the gifts, they're already $0). Same rule the
     purchase drawer uses so the two previews agree. */
  hasOfferCode() {
    return Boolean(this.cfg.offerCode) && this.cfg.offerCodePct > 0;
  }

  codeMult() {
    /* The offer's own code is the one this page attaches, so it sets the preview. */
    if (this.hasOfferCode()) return 1 - this.cfg.offerCodePct / 100;
    var hasCookie = /(?:^|;\s*)discount_code=/.test(document.cookie);
    return (hasCookie || document.body.classList.contains('discount-applied')) ? 0.9 : 1;
  }

  countFor(family, variantId) {
    var n = 0;
    this.picks[family].forEach(function (p) { if (p.variantId === variantId) n++; });
    return n;
  }

  unitsFor(family) { return this.picks[family].length; }

  requiredFor(family) { return family === 'soap' ? this.cfg.soapCount : this.cfg.deoCount; }

  linesFor(family) {
    var byVariant = {};
    var order = [];
    this.picks[family].forEach(function (p) {
      if (!byVariant[p.variantId]) { byVariant[p.variantId] = { variantId: p.variantId, price: p.price, qty: 0 }; order.push(p.variantId); }
      byVariant[p.variantId].qty++;
    });
    return order.map(function (id) { return byVariant[id]; });
  }

  totals() { return this.totalsFor(this.isSub()); }

  /* ----- picking ----- */

  pushPick(card) {
    var img = card.querySelector('img');
    this.picks[card.dataset.family].push({
      variantId: card.dataset.variantId,
      price: parseFloat(card.dataset.price) || 0,
      image: img ? (img.currentSrc || img.src) : '',
      label: card.dataset.title || ''
    });
  }

  add(card) {
    var family = card.dataset.family;
    if (!family || card.classList.contains('is-sold-out')) return;
    if (this.unitsFor(family) >= this.requiredFor(family)) return;
    this.pushPick(card);
    this.render();
  }

  remove(card) {
    var family = card.dataset.family;
    var id = card.dataset.variantId;
    for (var i = this.picks[family].length - 1; i >= 0; i--) {
      if (this.picks[family][i].variantId === id) { this.picks[family].splice(i, 1); break; }
    }
    this.render();
  }

  removeAt(family, index) {
    if (index < 0 || index >= this.picks[family].length) return;
    this.picks[family].splice(index, 1);
    this.render();
  }

  /* Back to the page's initial state (pre-selected defaults re-applied). */
  reset() {
    this.picks = { soap: [], deo: [] };
    this.applyDefaults();
    this.render();
    this.initCollapse();
  }

  /* ----- rendering ----- */

  render() {
    var self = this;
    ['soap', 'deo'].forEach(function (family) {
      var units = self.unitsFor(family);
      var required = self.requiredFor(family);
      var full = units >= required;

      self.querySelectorAll('[data-lbo-card][data-family="' + family + '"]').forEach(function (card) {
        var c = self.countFor(family, card.dataset.variantId);
        card.classList.toggle('is-picked', c > 0);
        card.classList.toggle('is-capped', full && c === 0);
        var count = card.querySelector('[data-lbo-count]');
        if (count) count.textContent = c;
        var stepper = card.querySelector('[data-lbo-stepper]');
        var addBtn = card.querySelector('[data-lbo-add]');
        if (stepper) stepper.hidden = c === 0;
        if (addBtn) { addBtn.hidden = c > 0; addBtn.disabled = full; }
        var plus = card.querySelector('[data-lbo-plus]');
        if (plus) plus.disabled = full;
      });

      var counter = self.querySelector('[data-lbo-picked="' + family + '"]');
      if (counter) counter.textContent = units + '/' + required;
      var group = self.querySelector('[data-lbo-group="' + family + '"]');
      if (group) group.classList.toggle('is-complete', full);
      var editButton = group && group.querySelector('[data-lbo-edit]');
      if (editButton) editButton.disabled = !full && !group.classList.contains('is-collapsed');

      self.renderSlots(family);
    });
    this.renderPrice();
    this.renderCta();
  }

  renderSlots(family) {
    var stage = this.querySelector('[data-lbo-slots="' + family + '"]');
    if (!stage) return;
    var required = this.requiredFor(family);
    var picks = this.picks[family];
    stage.innerHTML = '';
    for (var i = 0; i < required; i++) {
      var slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'lbo-slot';
      slot.setAttribute('data-lbo-slot', family);
      slot.setAttribute('data-index', String(i));
      var pick = picks[i];
      if (pick) {
        slot.classList.add('is-filled');
        slot.setAttribute('aria-label', 'Remove ' + pick.label);
        slot.title = pick.label + ' — click to remove';
        if (pick.image) {
          var img = document.createElement('img');
          img.src = pick.image;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          slot.appendChild(img);
        }
      } else {
        slot.disabled = true;
        slot.setAttribute('aria-label', 'Empty slot ' + (i + 1) + ' of ' + required);
      }
      stage.appendChild(slot);
    }
  }

  renderPrice() {
    var t = this.totals();
    var compare = this.querySelector('[data-lbo-compare]');
    var price = this.querySelector('[data-lbo-price]');
    var save = this.querySelector('[data-lbo-save]');
    if (compare) {
      compare.textContent = moneyFmtWhole(t.raw);
      // No crossed-out price when nothing is discounted.
      compare.hidden = !(t.discounted < t.raw);
    }
    if (price) price.textContent = moneyFmt(t.discounted);
    if (save) {
      var pct = savingsPercent(t.raw, t.discounted);
      save.textContent = pct > 0 ? 'Save ' + pct + '%' : '';
      save.hidden = pct <= 0;
    }
    this.classList.toggle('is-onetime', !this.isSub());

    // Plan cards: each shows its own total; highlight the checked one.
    var self = this;
    var subT = this.totalsFor(true);
    var oneT = this.totalsFor(false);
    var el;
    [['sub', subT], ['onetime', oneT]].forEach(function (pair) {
      var key = pair[0], tot = pair[1];
      var pct = savingsPercent(tot.raw, tot.discounted);
      var priceEl = self.querySelector('[data-lbo-plan-price="' + key + '"]');
      var compareEl = self.querySelector('[data-lbo-plan-compare="' + key + '"]');
      var saveEl = self.querySelector('[data-lbo-plan-save="' + key + '"]');
      if (priceEl) priceEl.textContent = moneyFmt(tot.discounted);
      if (compareEl) { compareEl.textContent = moneyFmtWhole(tot.raw); compareEl.hidden = pct <= 0; }
      if (saveEl) { saveEl.textContent = 'Save ' + pct + '%'; saveEl.hidden = pct <= 0; }
    });
    this.querySelectorAll('[data-lbo-plan]').forEach(function (input) {
      var card = input.closest('.lbo-plan');
      if (card) card.classList.toggle('is-selected', input.checked);
    });

    var ship = this.querySelector('[data-lbo-free-ship]');
    if (ship && this.cfg.freeShipDollars > 0) {
      ship.classList.toggle('is-unlocked', t.discounted >= this.cfg.freeShipDollars);
    }
    var code = this.querySelector('[data-lbo-code-note]');
    if (code) code.hidden = this.codeMult() >= 1;
  }

  renderCta() {
    var cta = this.querySelector('[data-lbo-cta]');
    var label = this.querySelector('[data-lbo-cta-label]');
    if (!cta || !label) return;
    if (this.submitting) return;
    var missingSoap = this.cfg.soapCount - this.unitsFor('soap');
    var missingDeo = this.cfg.deoCount - this.unitsFor('deo');
    var text;
    if (missingSoap > 0) {
      text = 'Pick ' + missingSoap + ' more ' + (missingSoap === 1 ? this.cfg.unit.soap.one : this.cfg.unit.soap.many);
    } else if (missingDeo > 0) {
      text = 'Pick ' + missingDeo + ' more ' + (missingDeo === 1 ? this.cfg.unit.deo.one : this.cfg.unit.deo.many);
    } else {
      text = this.cfg.ctaLabel + ' · ' + moneyFmt(this.totals().discounted);
    }
    var ready = missingSoap <= 0 && missingDeo <= 0;
    cta.disabled = !ready;
    cta.classList.toggle('is-disabled', !ready);
    label.textContent = text;
  }

  showError(message) {
    var box = this.querySelector('[data-lbo-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  clearError() {
    var box = this.querySelector('[data-lbo-error]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  /* ----- events ----- */

  onClick(e) {
    var slot = e.target.closest('[data-lbo-slot].is-filled');
    if (slot) { this.removeAt(slot.dataset.lboSlot, parseInt(slot.dataset.index, 10)); return; }

    var edit = e.target.closest('[data-lbo-edit]');
    if (edit) {
      var editFamily = edit.getAttribute('data-lbo-edit');
      var opening = this.isCollapsed(editFamily);
      /* Done only closes a complete family; an incomplete one stays open. */
      if (opening || this.unitsFor(editFamily) >= this.requiredFor(editFamily)) this.setCollapsed(editFamily, !opening);
      return;
    }

    var card = e.target.closest('[data-lbo-card]');
    if (card) {
      /* A collapsed list is a summary. Cards change picks only while it is open. */
      if (this.isCollapsed(card.dataset.family)) return;
      if (e.target.closest('[data-lbo-minus]')) { this.remove(card); return; }
      if (e.target.closest('[data-lbo-plus]') || e.target.closest('[data-lbo-add]')) { this.add(card); return; }
      if (e.target.closest('[data-lbo-count]')) return;
      this.add(card);
      return;
    }

    if (e.target.closest('[data-lbo-cta]')) { this.submit(); }
  }

  onChange(e) {
    if (e.target.matches('[data-lbo-plan]')) this.render();
  }

  /* ----- submit ----- */

  sellingPlanValue() {
    var raw = this.cfg.sellingPlan;
    return /^\d+$/.test(raw) ? parseInt(raw, 10) : raw;
  }

  createLoopTransaction(lines) {
    var cfg = this.cfg;
    if (!cfg.loopBundleId) return Promise.resolve(null);
    var body = {
      boxSizeId: cfg.loopBoxSizeId,
      discountId: null,
      sellingPlanShopifyId: this.sellingPlanValue(),
      productVariants: lines.map(function (l) { return { shopifyId: parseInt(l.variantId, 10), quantity: l.qty }; }),
      quantity: 1
    };
    return fetch('https://api.loopsubscriptions.com/storefront/2023-10/bundle/' + cfg.loopBundleId + '/transaction', {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Loop transaction request failed (' + r.status + ')');
        return r.json();
      })
      .then(function (data) {
        if (data && data.data && data.data.txnId) return data.data.txnId;
        console.warn('[launch-bundle] Loop response carried no txnId', data);
        return null;
      })
      .catch(function (err) {
        // Same policy as the purchase drawer: a Loop outage must not block
        // the sale. The line still carries the selling plan; only the
        // Loop grouping id is missing.
        console.error('[launch-bundle] Loop transaction failed, continuing without txnId', err);
        return null;
      });
  }

  submit() {
    var self = this;
    var t = this.totals();
    if (!t.complete || this.submitting) return;
    this.clearError();

    if (!this.cfg.offerKey) {
      console.error('[launch-bundle] no offer name: set the offer_key setting so cart lines carry _offer');
      this.showError('This bundle is not set up yet. Please try again later.');
      return;
    }

    var subscribe = this.isSub();
    var lines = this.linesFor('soap').concat(this.linesFor('deo'));
    var cta = this.querySelector('[data-lbo-cta]');
    var label = this.querySelector('[data-lbo-cta-label]');
    this.submitting = true;
    if (cta) cta.disabled = true;
    if (label) label.textContent = 'Adding…';

    function unlock() {
      self.submitting = false;
      self.renderCta();
    }

    var txnPromise = subscribe ? this.createLoopTransaction(lines) : Promise.resolve(null);

    txnPromise.then(function (txnId) {
      var items = buildCartItems(
        { soap: self.linesFor('soap'), deo: self.linesFor('deo') },
        self.gifts,
        {
          subscribe: subscribe,
          groupId: Math.floor(1000 + Math.random() * 9000).toString(),
          txnId: txnId,
          sellingPlan: self.sellingPlanValue(),
          bundleName: self.cfg.bundleName,
          giftLabel: self.cfg.giftLabel,
          giftsPaid: self.cfg.giftsPaid,
          offerKey: self.cfg.offerKey,
          offerHandle: self.cfg.offerHandle
        }
      );

      var cartDrawer = document.querySelector('cart-drawer');
      var wantsDrawer = self.cfg.afterAdd === 'cart' && cartDrawer && typeof cartDrawer.renderContents === 'function';
      var sections = wantsDrawer ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; }) : null;

      var addPayload = { items: items };
      /* One code per offer: the offer discount code, or the free-gift code on
         a free-gift offer. Paid accessories never use the gift code. */
      var codeToAttach = self.cfg.offerCode || (self.cfg.giftsPaid ? '' : self.cfg.giftCode);
      if (wantsDrawer && !codeToAttach) {
        addPayload.sections = sections;
        addPayload.sections_url = window.location.pathname;
      }

      return fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addPayload)
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            throw new Error((result.data && (result.data.description || result.data.message)) || ('Cart add failed (' + result.status + ')'));
          }
          if (!codeToAttach) return result.data;
          return self.attachGiftCode(sections, codeToAttach);
        })
        .then(function (state) {
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'launch-bundle-offer', cartData: state });
          }
          if (wantsDrawer && state && state.sections) {
            cartDrawer.renderContents(state);
            self.reset();
            unlock();
            return;
          }
          window.location.href = self.cfg.afterAdd === 'cart' ? window.Shopify.routes.root + 'cart' : window.Shopify.routes.root + 'checkout';
        })
        .catch(function (err) {
          console.error('[launch-bundle] submit failed', err);
          self.showError('Something went wrong adding your bundle. Please try again.');
          unlock();
        });
    });
  }

  /* Attach the gift discount code to the cart (the code is what makes the
     tray + bags $0 at checkout). Existing codes on the cart are kept. */
  attachGiftCode(sections, code) {
    var self = this;
    return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .catch(function (err) {
        console.warn('[launch-bundle] could not read existing cart codes', err);
        return {};
      })
      .then(function (cart) {
        var existing = (cart && cart.discount_codes) || [];
        var payload = { discount: mergeDiscountCodes(existing, code) };
        if (sections) { payload.sections = sections; payload.sections_url = window.location.pathname; }
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
      .then(function (result) {
        if (!result.ok) throw new Error('Could not apply the offer code (' + result.status + ')');
        return result.data;
      });
  }
}

if (!customElements.get('launch-bundle-offer')) {
  customElements.define('launch-bundle-offer', LaunchBundleOffer);
}
